// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CartPageTemplate, { CartPageTemplateStyles } from "@eisinc/App/Components/Cart/CartPageTemplate";
import XngZone from "@eisinc/App/Components/Common/XngZone";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

export interface CartPageContainerStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    fulfillmentMethodContainer?: InjectableCss;
    cartTotalContainer?: InjectableCss;
    productCarouselContainer?: InjectableCss;
    // XNG-Changes
    title?: TypographyProps;
    cartPageTemplate?: CartPageTemplateStyles;
    wrapper?: InjectableCss;
}

export const cartPageContainerStyles: CartPageContainerStyles = {
    container: {
        css: css`
            /* stylelint-disable value-no-vendor-prefix */
            display: -ms-grid;
            display: grid;
            padding: 1.5rem 0 0 1.5rem;
            margin: -1.5rem;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: minmax(0, 1fr);
                            -ms-grid-columns: minmax(0, 1fr);
                        `,
                        null,
                        css`
                            grid-template-columns: repeat(2, minmax(0, 1fr));
                            -ms-grid-columns: minmax(0, 1fr) minmax(0, 1fr);
                            grid-template-rows: repeat(5, auto);
                            -ms-grid-rows: auto auto auto auto auto;
                        `,
                        css`
                            grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
                            -ms-grid-columns: minmax(0, 2fr) minmax(0, 1fr);
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-column-end: 3;
                            -ms-grid-column-span: 3;
                            grid-row-start: 1;
                            -ms-grid-row: 1;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartLinesContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                            grid-row-end: 6;
                            -ms-grid-row-span: 6;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    fulfillmentMethodContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartTotalContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 3;
                            -ms-grid-row: 3;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    productCarouselContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 4;
                            -ms-grid-row: 4;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = cartPageContainerStyles;

// XNG-Change: use CartPageTemplate layout
const CartPageContainer = ({ id }: WidgetProps) => {
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);

    return (
        <StyledWrapper {...styles.wrapper}>
            <CartPageTemplate extendedStyles={styles.cartPageTemplate}>
                <CartPageTemplate.HeaderSection>
                    <CartPageTemplate.TitleSection>
                        <Typography {...styles.title}>{translate("Cart")}</Typography>
                        {isMobileScreen && <XngZone zoneName="Content2" contentId={id} hint="Cart - CartTotal" />}
                    </CartPageTemplate.TitleSection>
                    <CartPageTemplate.CartApproverMessage>
                        <XngZone zoneName="Content3" contentId={id} hint="Cart - Cart Messages" />
                    </CartPageTemplate.CartApproverMessage>
                </CartPageTemplate.HeaderSection>
                <CartPageTemplate.CartTotalSection>
                    <XngZone zoneName="Content2" contentId={id} hint="Cart - CartTotal" />
                    <XngZone zoneName="Content5" contentId={id} hint="Cart - Authored Content" />
                </CartPageTemplate.CartTotalSection>
                <CartPageTemplate.MainSection>
                    <CartPageTemplate.ActionsSection>
                        <XngZone zoneName="Content4" contentId={id} hint="Cart - Cart Actions" />
                    </CartPageTemplate.ActionsSection>
                    <XngZone zoneName="Content1" contentId={id} hint="Cart - CartLines table" />
                </CartPageTemplate.MainSection>
            </CartPageTemplate>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CartPageContainer,
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage"],
        displayName: "Page Container",
    },
};

export default widgetModule;
